import { assertNotEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useState } from 'react'

import {
  TransferButton,
  type TransferConfirmation,
} from '../../components/transferButton/TransferButton'
import { getDefaultTransferScope } from '../../components/transferScopeSelector/getDefaultTransferScope'
import {
  type TransferScope,
  TransferScopeSelector,
} from '../../components/transferScopeSelector/TransferScopeSelector'
import { useEnumId } from '../../hooks/useEnumId'
import { useNavigationParams } from '../../system/useNavigationParams'
import { createOperationName } from '../../uiModels/annulOperations/helpers'
import type { ConfiguredEnumLiteral } from '../../uiModels/enumLiterals/types'
import { detectEnumTransferConflict } from '../../uiModels/transfer/detectEnumTransferConflict'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import { useOperationsContext } from '../../workspace/operationsContext'

/**
 * Transfers the enum in scope to an ancestor block, either its definition, its
 * literals, or both.
 */
export const EnumTransferButton = () => {
  const translate = useTranslate()
  const enumId = useEnumId()
  const { block: currentViewBlock } = useNavigationParams()
  const { applicationEnumDefinitions } = useApplicationViewOperationsContext()
  const { configuredEnums } = useOperationsContext()

  const [selectedScope, setSelectedScope] = useState<TransferScope>('both')

  const configuredEnumForId = applicationEnumDefinitions.get(enumId)

  const transferableDefinitionVersions =
    configuredEnumForId?.block === currentViewBlock &&
    configuredEnumForId.internalOperation &&
    !configuredEnumForId.annulOperations.length &&
    !configuredEnumForId.isDraft
      ? [
          {
            ...configuredEnumForId,
            // Required for type inference to understand that the definition is defined
            internalOperation: configuredEnumForId.internalOperation,
          },
        ]
      : []

  const transferableLiteralVersions = (
    configuredEnumForId?.literals ?? []
  ).filter(
    (literal): literal is ConfiguredEnumLiteral =>
      literal.type === 'configured' &&
      literal.block === currentViewBlock &&
      !literal.annulOperations.length &&
      !literal.isDraft,
  )

  const isDefinitionAvailable = transferableDefinitionVersions.length > 0
  const isLiteralsAvailable = transferableLiteralVersions.length > 0

  const scopeVersionCount = (() => {
    if (selectedScope === 'primary') {
      return transferableDefinitionVersions.length
    }
    if (selectedScope === 'associated') {
      return transferableLiteralVersions.length
    }
    return (
      transferableDefinitionVersions.length + transferableLiteralVersions.length
    )
  })()

  const hasConflict = (destinationBlock: string) => {
    const conflictType = detectEnumTransferConflict({
      configuredEnums,
      enumName: enumId,
      destinationBlock,
      transferringLiteralNames: transferableLiteralVersions.map(
        ({ internalOperation }) => internalOperation.entity?.name ?? '',
      ),
    })

    if (selectedScope === 'primary') {
      return conflictType === 'definition' || conflictType === 'both'
    }
    if (selectedScope === 'associated') {
      return conflictType === 'literal' || conflictType === 'both'
    }
    return conflictType !== 'none'
  }

  const handleConfirm: TransferConfirmation = async ({ transfer }) => {
    let addedToWorkspaceKey = undefined

    const shouldTransferDefinition =
      selectedScope === 'primary' ||
      (selectedScope === 'both' && isDefinitionAvailable)
    const shouldTransferLiterals =
      selectedScope === 'associated' ||
      (selectedScope === 'both' && isLiteralsAvailable)

    if (shouldTransferDefinition) {
      addedToWorkspaceKey = await transfer({
        operationName: enumId,
        operations: transferableDefinitionVersions,
        shouldInvalidateCacheAfterTransfer: !shouldTransferLiterals,
      })
    }
    if (shouldTransferLiterals) {
      const transferableLiteralOperationNames = new Set<string>()
      transferableLiteralVersions.forEach(({ internalOperation }) => {
        assertNotEmpty(internalOperation.entity?.enumName)
        assertNotEmpty(internalOperation.entity.name)
        transferableLiteralOperationNames.add(
          createOperationName(internalOperation),
        )
      })

      const allLiteralTransfers = Array.from(
        transferableLiteralOperationNames,
      ).map((operationName, index) =>
        transfer({
          operationName,
          operations: transferableLiteralVersions.filter(
            ({ internalOperation }) =>
              createOperationName(internalOperation) === operationName,
          ),
          shouldInvalidateCacheAfterTransfer:
            index === transferableLiteralOperationNames.size - 1, // Skip unnecessary cache invalidation for all but the last literal transfer
        }),
      )

      const settledLiteralTransfers =
        await Promise.allSettled(allLiteralTransfers)

      for (const result of settledLiteralTransfers) {
        if (result.status === 'fulfilled' && result.value) {
          addedToWorkspaceKey = result.value
          break
        }
      }
    }

    return addedToWorkspaceKey
  }

  return (
    <TransferButton
      item={{
        itemId: enumId,
        type: 'enumDefinition',
        operationName: enumId,
        versionCount: scopeVersionCount,
        isTransferable: isDefinitionAvailable || isLiteralsAvailable,
      }}
      transfer={{
        label: translate('enumViewer.topBar.actions.transfer'),
        description: translate('transfer.confirmationModal.enum.description', {
          sourceBlock: currentViewBlock,
        }),
        getConflictWarning: (destinationBlock) =>
          hasConflict(destinationBlock)
            ? translate('transfer.confirmationModal.conflict.warning')
            : undefined,
        onOpen: () =>
          setSelectedScope(
            getDefaultTransferScope({
              isPrimaryAvailable: isDefinitionAvailable,
              isAssociatedAvailable: isLiteralsAvailable,
            }),
          ),
        onConfirm: handleConfirm,
      }}
    >
      <TransferScopeSelector
        value={selectedScope}
        onChange={setSelectedScope}
        primaryLabel={translate('transfer.scope.primary.definition')}
        associatedLabel={translate('transfer.scope.associated.enumLiterals')}
        isPrimaryAvailable={isDefinitionAvailable}
        isAssociatedAvailable={isLiteralsAvailable}
      />
    </TransferButton>
  )
}
